import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { fmtMoney } from "../lib/api";
import { getCategoryMeta } from "../lib/categoryIcons";
import { ChevronDown, ChevronRight, ArrowRight } from "lucide-react";

const EXPENSE_CATS = ["Groceries", "Food", "Rent/Mortgage", "Utility Bills", "Internet", "Phone Bill", "Credit Cards", "Loans", "Transport", "Gas", "Travel", "Subscriptions", "Fitness", "Payments", "Shopping", "Entertainment", "Health", "Education", "Other"];
const INCOME_CATS = ["Salary", "Freelance", "Investment", "Gift", "Other"];

export default function StatementPreviewDialog({ open, onOpenChange, extracted, currency, onConfirm, saving, meta }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [viewMode, setViewMode] = useState("grouped"); // "grouped" | "list"
  const [collapsed, setCollapsed] = useState({}); // { [category]: true/false }

  React.useEffect(() => {
    if (open) {
      setRows(extracted.map((t, i) => ({ ...t, _idx: i })));
      const initial = {};
      // Default: select non-duplicates only. Users can still opt-in.
      extracted.forEach((t, i) => { initial[i] = !t.is_duplicate; });
      setSelected(initial);
      setCollapsed({});
    }
  }, [open, extracted]);

  const total = rows.length;
  const selectedCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);
  const allSelected = total > 0 && selectedCount === total;

  const toggle = (i) => setSelected(s => ({ ...s, [i]: !s[i] }));
  const toggleAll = () => {
    const next = !allSelected;
    const s = {};
    rows.forEach((r) => { s[r._idx] = next; });
    setSelected(s);
  };
  const updateRow = (i, patch) => {
    setRows(rs => rs.map(r => (r._idx === i ? { ...r, ...patch } : r)));
  };

  // Group rows by category (preserving order of first appearance)
  const groups = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      const key = r.category || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    // Convert to array with stats and sort by total desc (expenses positive)
    const arr = Array.from(map.entries()).map(([category, items]) => {
      const totalAmount = items.reduce((s, x) => s + Number(x.amount || 0), 0);
      const isIncome = items[0]?.type === "income";
      const selectedInGroup = items.filter(x => selected[x._idx]).length;
      return {
        category,
        items,
        totalAmount,
        isIncome,
        selectedInGroup,
        count: items.length,
        allSelected: selectedInGroup === items.length && items.length > 0,
        someSelected: selectedInGroup > 0 && selectedInGroup < items.length,
      };
    });
    arr.sort((a, b) => b.totalAmount - a.totalAmount);
    return arr;
  }, [rows, selected]);

  const toggleGroupSelected = (group) => {
    const target = !group.allSelected;
    setSelected(s => {
      const next = { ...s };
      group.items.forEach(r => { next[r._idx] = target; });
      return next;
    });
  };
  const toggleGroupCollapsed = (cat) => {
    setCollapsed(c => ({ ...c, [cat]: !c[cat] }));
  };
  const reassignGroup = (fromCategory, toCategory) => {
    if (!toCategory || toCategory === fromCategory) return;
    setRows(rs => rs.map(r => (r.category === fromCategory ? { ...r, category: toCategory } : r)));
  };

  const confirm = () => {
    const toSave = rows.filter(r => selected[r._idx]).map(r => ({
      type: r.type,
      amount: Number(r.amount),
      category: r.category,
      description: r.description || "",
      date: r.date,
      source: "statement",
    }));
    onConfirm(toSave);
  };

  const renderRow = (r, indentLeft = false) => {
    const meta = getCategoryMeta(r.category);
    const Ico = meta.Icon;
    const isChecked = !!selected[r._idx];
    const catList = r.type === "income" ? INCOME_CATS : EXPENSE_CATS;
    const suggested = r.suggested_category && r.suggested_category !== r.category ? r.suggested_category : null;
    return (
      <div
        key={r._idx}
        data-testid={`preview-row-${r._idx}`}
        className={`flex items-center gap-2.5 sm:gap-3 rounded-xl border p-2.5 sm:p-3 transition-opacity ${isChecked ? "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" : "border-slate-100 dark:border-slate-800 opacity-45"} ${indentLeft ? "ml-2 sm:ml-8" : ""}`}
      >
        <Checkbox
          checked={isChecked}
          onCheckedChange={() => toggle(r._idx)}
          data-testid={`preview-check-${r._idx}`}
        />
        {!indentLeft && (
          <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${meta.bg} ${meta.fg}`}>
            <Ico className="w-4 h-4" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
            {r.description || r.category}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5 flex-wrap">
            <span>{r.date ? new Date(r.date).toLocaleDateString() : "—"}</span>
            {suggested && (
              <button
                type="button"
                data-testid={`preview-suggest-${r._idx}`}
                onClick={() => updateRow(r._idx, { category: suggested })}
                title={`AI picked ${r.category} — click to reassign to ${suggested}`}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-500/30 transition-colors"
              >
                Try {suggested} →
              </button>
            )}
          </div>
        </div>
        <Select value={r.category} onValueChange={(v) => updateRow(r._idx, { category: v })} disabled={!isChecked}>
          <SelectTrigger className="w-[120px] sm:w-[140px] rounded-full h-8 sm:h-9 text-xs" data-testid={`preview-cat-${r._idx}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {catList.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className={`w-20 sm:w-24 text-right tabular text-xs sm:text-sm font-semibold ${r.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>
          {r.type === "income" ? "+" : "-"}{fmtMoney(r.amount, currency)}
        </div>
      </div>
    );
  };

  const grandTotal = useMemo(() => {
    return rows.reduce((s, r) => {
      if (!selected[r._idx]) return s;
      return s + (r.type === "income" ? Number(r.amount) : -Number(r.amount));
    }, 0);
  }, [rows, selected]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="statement-preview-dialog" className="rounded-3xl bg-slate-50 dark:bg-slate-950 sm:max-w-3xl max-h-[88vh] flex flex-col p-0">
        <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6">
          <DialogTitle className="font-display text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Review extracted transactions</DialogTitle>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            The AI grouped {total} transaction{total !== 1 ? "s" : ""} across {groups.length} categor{groups.length !== 1 ? "ies" : "y"}. Reassign any group or edit rows individually.
          </p>
          {(meta?.ocrUsed || meta?.period?.hint) && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="preview-meta-chips">
              {meta.ocrUsed && (
                <span
                  data-testid="preview-ocr-badge"
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
                  title="This PDF had no extractable text — OCR was used via Claude vision. Double-check amounts before saving."
                >
                  <span>👁</span> OCR'd
                </span>
              )}
              {meta?.period?.hint && (
                <span
                  data-testid="preview-period-badge"
                  className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                  title="Detected statement period — used to anchor date years"
                >
                  Period · {meta.period.hint}
                </span>
              )}
            </div>
          )}
        </DialogHeader>

        <div className="flex items-center justify-between px-5 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-800 gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <button data-testid="toggle-all-btn" onClick={toggleAll} className="text-xs font-semibold spidey-red hover:underline">
              {allSelected ? "Unselect all" : "Select all"}
            </button>
            <div className="text-xs text-slate-500 dark:text-slate-400 font-semibold">
              {selectedCount} of {total} selected
            </div>
          </div>
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-full p-1" data-testid="preview-view-toggle">
            {[
              { k: "grouped", label: "By category" },
              { k: "list", label: "Chronological" },
            ].map(v => (
              <button
                key={v.k}
                data-testid={`preview-view-${v.k}`}
                onClick={() => setViewMode(v.k)}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${viewMode === v.k ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}
              >{v.label}</button>
            ))}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-3 space-y-3">
          {rows.length === 0 && (
            <div className="text-center text-slate-500 dark:text-slate-400 text-sm py-12">No rows to preview.</div>
          )}

          {viewMode === "list" && rows.map(r => renderRow(r))}

          {viewMode === "grouped" && groups.map(g => {
            const meta = getCategoryMeta(g.category);
            const Ico = meta.Icon;
            const isCollapsed = !!collapsed[g.category];
            const catList = g.isIncome ? INCOME_CATS : EXPENSE_CATS;
            const groupPct = total > 0 ? Math.round((g.count / total) * 100) : 0;
            return (
              <div
                key={g.category}
                data-testid={`preview-group-${g.category}`}
                className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900"
              >
                {/* Group header */}
                <div className={`flex items-center gap-2 sm:gap-3 px-3 py-2.5 border-b border-slate-100 dark:border-slate-800 ${meta.bg} bg-opacity-40 dark:bg-opacity-20`}>
                  <Checkbox
                    checked={g.allSelected}
                    onCheckedChange={() => toggleGroupSelected(g)}
                    data-testid={`preview-group-check-${g.category}`}
                    className={g.someSelected ? "data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-600" : ""}
                    aria-label={`Select all ${g.category}`}
                  />
                  <button
                    type="button"
                    onClick={() => toggleGroupCollapsed(g.category)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    data-testid={`preview-group-header-${g.category}`}
                  >
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg grid place-items-center shrink-0 ${meta.bg} ${meta.fg}`}>
                      <Ico className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate flex items-center gap-1.5">
                        {g.category}
                        {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                      </div>
                      <div className="text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 font-semibold">
                        {g.selectedInGroup}/{g.count} · {groupPct}% of statement
                      </div>
                    </div>
                  </button>
                  {/* Bulk reassign for whole group */}
                  <div className="hidden sm:flex items-center gap-1.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400 shrink-0">
                    <ArrowRight className="w-3 h-3" />
                    <Select value={g.category} onValueChange={(v) => reassignGroup(g.category, v)}>
                      <SelectTrigger className="w-[120px] rounded-full h-8 text-xs" data-testid={`preview-group-reassign-${g.category}`} aria-label="Move whole group">
                        <SelectValue placeholder="Move all" />
                      </SelectTrigger>
                      <SelectContent>
                        {catList.map(c => <SelectItem key={c} value={c} disabled={c === g.category}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={`tabular text-sm font-bold shrink-0 ${g.isIncome ? "text-emerald-600" : "text-rose-600"}`}>
                    {g.isIncome ? "+" : "-"}{fmtMoney(g.totalAmount, currency)}
                  </div>
                </div>

                {/* Rows in group */}
                {!isCollapsed && (
                  <div className="p-2 sm:p-3 space-y-2">
                    {g.items.map(r => renderRow(r, true))}
                    {/* Mobile-only reassign in the body */}
                    <div className="sm:hidden pt-1 flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400 font-semibold">
                      <ArrowRight className="w-3 h-3" /> Move whole group
                      <Select value={g.category} onValueChange={(v) => reassignGroup(g.category, v)}>
                        <SelectTrigger className="flex-1 rounded-full h-8 text-xs" data-testid={`preview-group-reassign-m-${g.category}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {catList.map(c => <SelectItem key={c} value={c} disabled={c === g.category}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="px-5 sm:px-6 py-4 border-t border-slate-200 dark:border-slate-800 gap-2 sm:justify-between flex-col sm:flex-row items-stretch sm:items-center bg-white dark:bg-slate-900">
          <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 font-semibold">
            Net impact: <span className={`tabular font-bold ${grandTotal >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {grandTotal >= 0 ? "+" : "-"}{fmtMoney(Math.abs(grandTotal), currency)}
            </span>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="preview-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={confirm}
              disabled={selectedCount === 0 || saving}
              className="rounded-full bg-spidey-red text-white hover:opacity-90"
              data-testid="preview-confirm-btn"
            >
              {saving ? "Saving…" : selectedCount === 0 ? "Nothing selected" : `Save ${selectedCount} transaction${selectedCount > 1 ? "s" : ""}`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
